import { Modal, Button, Typography, Space } from 'antd';
import { WarningOutlined, LogoutOutlined, WalletOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';
import { isAdmin, isSupportUser } from '../../utils/roleHelpers';
import { useGetSubscriptionStatusQuery } from '../../services/stripeApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';

const { Title, Text } = Typography;

const TrialExpiredModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isTrialExpired, trialEndDate, plan, user, isAuthenticated } = useAppSelector(state => state.auth);
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Get subscription status directly from Stripe API
  const { data: subscriptionStatus } = useGetSubscriptionStatusQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Check if user is admin
  const userIsAdmin = isAdmin(user?.role) || isSupportUser(user?.email);

  // Check if subscription is active in Stripe (any plan, not just the 3 known ones)
  const hasActiveSubscription = subscriptionStatus?.hasSubscription &&
    (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing');

  // Check if trial period is still valid (from database TrialEndDate)
  const isTrialStillValid = trialEndDate && new Date(trialEndDate) > new Date();

  // Check if user is on subscription management page
  const isOnSubscriptionPage = location.pathname === '/subscription' || location.pathname === '/select-plan';

  // Show modal when:
  // 1. No active subscription in Stripe
  // 2. Trial period has expired (trialEndDate is in the past OR isTrialExpired is true)
  // 3. plan is 'expired'
  // Don't show when:
  // - Has any active subscription in Stripe
  // - Trial period is still valid (trialEndDate is in the future)
  // - User is on subscription management page (so they can manage their subscription)
  //
  // La prueba que ya pasó su fecha también cuenta, aunque isTrialExpired siga en false:
  // ese flag se refresca desde el servidor y, si la sesión se abrió antes del
  // vencimiento, un empleado podía seguir cobrando días después de que terminara.
  const trialDatePassed = plan === 'trial' && !!trialEndDate && new Date(trialEndDate) <= new Date();
  const shouldShow = !hasActiveSubscription &&
                     !isTrialStillValid &&
                     !isOnSubscriptionPage &&
                     (isTrialExpired || plan === 'expired' || trialDatePassed);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleManageSubscription = () => {
    navigate('/subscription');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return formatInTimeZone(dateString, 'D [de] MMMM [de] YYYY');
  };

  return (
    <Modal
      open={shouldShow}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={480}
      zIndex={2000}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <WarningOutlined style={{ fontSize: 64, color: '#faad14', marginBottom: 24 }} />

        <Title level={3} style={{ marginBottom: 8 }}>
          {plan === 'expired' ? 'Tu suscripción ha expirado' : 'Tu periodo de prueba ha terminado'}
        </Title>

        {trialEndDate && plan !== 'expired' && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            Tu prueba gratuita finalizó el {formatDate(trialEndDate)}
          </Text>
        )}

        {userIsAdmin ? (
          <>
            <Text style={{ display: 'block', marginBottom: 32, fontSize: 16 }}>
              Para continuar utilizando Massrestaurant, por favor activa o renueva tu suscripción.
            </Text>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                size="large"
                block
                icon={<WalletOutlined />}
                onClick={handleManageSubscription}
                style={{ background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
              >
                Administrar Suscripción
              </Button>

              <Button
                size="large"
                block
                icon={<LogoutOutlined />}
                onClick={handleLogout}
              >
                Cerrar Sesión
              </Button>
            </Space>
          </>
        ) : (
          <>
            <Text style={{ display: 'block', marginBottom: 16, fontSize: 16 }}>
              El acceso al sistema está temporalmente suspendido.
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 32, fontSize: 14 }}>
              Por favor, contacta al administrador de tu organización para renovar la suscripción.
            </Text>

            <Button
              size="large"
              block
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Cerrar Sesión
            </Button>
          </>
        )}

        <Text type="secondary" style={{ display: 'block', marginTop: 24, fontSize: 12 }}>
          ¿Tienes preguntas? Contáctanos en info@massresto.com
        </Text>
      </div>
    </Modal>
  );
};

export default TrialExpiredModal;
